import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { IndicatorChartTypeEnums } from 'src/app/enum/indicator-chart-enums';
import { InvoicingCutoffEnums } from 'src/app/enum/invoicing-cutoff-enums';
import { OptionWindow } from 'src/app/enum/option-window-enums';
import { ProfileUserEnums } from 'src/app/enum/profile-user-enums';
import { WindowEnums } from 'src/app/enum/window-enums';
import { IndicatorChart } from 'src/app/interfaces/custom-indicators/indicator-chart.interface';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { OptionWindowModel } from 'src/app/models/authentication/option-window.model';
import { UserModel } from 'src/app/models/authentication/user.model';
import { ItemChartModel } from 'src/app/models/charts/global/item-chart.model';
import { InvoicingCutoffChartModel } from 'src/app/models/charts/invoicing/invoicing-cutoff-chart.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { InvoicingCutoffDetailTableModel } from 'src/app/models/tables/invoicing/invoicing-cutoff-detail-table.model';
import { InvoicingCutoffTableModel } from 'src/app/models/tables/invoicing/invoicing-cutoff-table.model';
import { OptionWindowService } from 'src/app/services/authentication/option-window.service';
import { WindowService } from 'src/app/services/authentication/window.service';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { InvoicingCutoffDetailService } from 'src/app/services/invoicing/invoicing-cutoff-detail.service';
import { InvoicingCutoffService } from 'src/app/services/invoicing/invoicing-cutoff.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

const InvoicingCutoffIndicatorsConstants = {
  states: [
    { id: InvoicingCutoffEnums.estadoAbierto, label: 'Abiertos', color: '#EF4444' },
    { id: InvoicingCutoffEnums.estadoPrefacturado, label: 'Prefacturados', color: '#FB923C' },
    { id: InvoicingCutoffEnums.estadoFacturado, label: 'Facturados', color: '#198754' },
    { id: InvoicingCutoffEnums.estadoSuspendido, label: 'Suspendidos', color: '#3B82F6' }
  ]
};
@Component({
  selector: 'app-invoicing-cutoff',
  templateUrl: './invoicing-cutoff.component.html',
  styleUrls: ['./invoicing-cutoff.component.scss'],
  standalone: false
})
export class InvoicingCutoffComponent implements OnInit, OnDestroy {

  activeTabIndex = 0;
  ascendant: boolean = true;
  columnSort: string | null = null;
  detailData: PagedResponseModel<InvoicingCutoffDetailTableModel> = new PagedResponseModel<InvoicingCutoffDetailTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  filters: ColumnResponseModel[] = [];
  generalData: PagedResponseModel<InvoicingCutoffTableModel> = new PagedResponseModel<InvoicingCutoffTableModel>();
  generalHeader: ColumnResponseModel[] = [];
  indicatorCharts: IndicatorChart[] = this.buildIndicatorCharts(new InvoicingCutoffChartModel());
  indicatorsRequestId: number = 0;
  isOpened: string | undefined = undefined;
  limit: number = 10;
  loadingIndicators: boolean = false;
  nameWindow: string = 'Cortes de Facturación';
  page: number = 1;
  permissionUpdatable: boolean = false;
  permissionsUser: Array<OptionWindowModel> = new Array<OptionWindowModel>();
  projectName = ProjectName.projectName;
  tableActions: TableAction[] = [
    {
      label: 'Ver',
      icon: 'pi pi-eye',
      action: 'edit'
    },
  ];
  url: string = "";
  userAuthenticated: UserModel = new UserModel();

  constructor(private headerService: HeaderService,
    private invoicingCutoffService: InvoicingCutoffService,
    private invoicingCutoffDetailService: InvoicingCutoffDetailService,
    private optionWindowService: OptionWindowService,
    private userAuthenticatedService: UserAuthenticatedService,
    private router: Router,
    private title: Title,
    private toastHandlerService: ToastHandlerService,
    private windowService: WindowService,) {
    this.getPermission();
  }

  async ngOnInit() {
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    const filters = localStorage.getItem('invoicing-cutoff');
    if (filters) this.filters = JSON.parse(filters);
    this.getIndicatorsForGeneralConsultation();
    await Promise.all([
      this.getColumnsForGeneralConsultation(),
      this.getInvoicingCutoffsWithPagination(),
    ]);
    this.headerService.setTitle(this.nameWindow);
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  async ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  buildIndicatorCharts(indicators: InvoicingCutoffChartModel): IndicatorChart[] {
    const { states } = InvoicingCutoffIndicatorsConstants;
    return [
      {
        type: IndicatorChartTypeEnums.donut,
        title: 'Cortes por estado',
        labels: states.map(state => state.label),
        values: states.map(state => this.getIndicatorQuantity(indicators.states, state.id)),
        colors: states.map(state => state.color),
        total: indicators.total
      }
    ];
  }

  createNewRegister() {
    if (this.permissionUpdatable) {
      this.router.navigate(['/invoicing-cutoff/register']);
    } else {
      this.toastHandlerService.showToast(
        'error',
        'El usuario no tiene permisos para crear cortes de facturación. Comuníquese con el administrador del sistema.'
      );
    }
  }

  async exportDetailsConsultation() {
    if (this.userAuthenticated.profileUser.code == ProfileUserEnums.administrador || this.permissionsUser.find(x => x.code == OptionWindow.Exportar)) {
      try {
        const response = await firstValueFrom(this.invoicingCutoffDetailService.exportDetailConsultation(this.filters, this.columnSort, this.ascendant));
        const blob = response;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Cortes de Facturación Detalles.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error al exportar el detalle de los cortes de facturación.', error);
      }
    } else {
      this.toastHandlerService.showToast(
        'warning',
        'El usuario no tiene permisos para exportar el detalle de los cortes de facturación, comuniquese con el administrador del sistema.'
      );
    }
  }

  async exportGeneralConsultation() {
    if (this.userAuthenticated.profileUser.code == ProfileUserEnums.administrador || this.permissionsUser.find(x => x.code == OptionWindow.Exportar)) {
      try {
        const response = await firstValueFrom(this.invoicingCutoffService.exportGeneralConsultation(this.filters, this.columnSort, this.ascendant));
        const blob = response;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Cortes de Facturacion.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error al exportar los cortes de facturación.', error);
      }
    } else {
      this.toastHandlerService.showToast(
        'warning',
        'El usuario no tiene permisos para exportar los cortes de facturación, comuniquese con el administrador del sistema.'
      );
    }
  }

  async getColumnsForDetailsConsultation() {
    try {
      if (this.detailHeader.length === 0) {
        this.detailHeader = await firstValueFrom(this.invoicingCutoffDetailService.getColumnsForDetailConsultation());
      }
    } catch (error) {
      console.error('Error al obtener las columnas para la consulta de detalles.', error);
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.generalHeader.length === 0) {
        this.generalHeader = await firstValueFrom(this.invoicingCutoffService.getColumnsForGeneralConsultation());
      }
    } catch (error) {
      console.error('Error al obtener las columnas para la consulta general.', error);
    }
  }

  async getDetailsWithPagination() {
    try {
      this.detailData = await firstValueFrom(
        this.invoicingCutoffDetailService.getAllInvoicingCutoffDetailsWithPagination(this.page, this.limit, this.columnSort, this.ascendant, this.filters)
      );
    } catch (error) {
      console.error('Error al buscar los detalles de los cortes de facturación.', error);
    }
  }

  async getIndicatorsForGeneralConsultation() {
    // Descarta respuestas de solicitudes anteriores que lleguen después de la última
    const requestId = ++this.indicatorsRequestId;
    this.loadingIndicators = true;
    try {
      const indicators = await firstValueFrom(this.invoicingCutoffService.getIndicatorsForGeneralConsultation(this.filters));
      if (requestId === this.indicatorsRequestId) {
        this.indicatorCharts = this.buildIndicatorCharts(indicators);
      }
    } catch (error) {
      console.error('Error al buscar los indicadores de los cortes de facturación.', error);
    } finally {
      if (requestId === this.indicatorsRequestId)
        this.loadingIndicators = false;
    }
  }

  getIndicatorQuantity(items: ItemChartModel[], id: number): number {
    return items?.find(item => item.id === id)?.quantity ?? 0;
  }

  async getInvoicingCutoffsWithPagination() {
    try {
      this.generalData = await firstValueFrom(
        this.invoicingCutoffService.getAllInvoicingCutoffsWithPagination(this.page, this.limit, this.columnSort, this.ascendant, this.filters)
      );
    } catch (error) {
      console.error('Error al buscar los cortes de facturación.', error);
    }
  }

  async getPermission() {
    let url = this.router.url as string
    url = url.substring(1);
    this.url = url;
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'error',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
    } catch (error) {
      console.error('Error al obtener el perfil de permisos por URL.', error);
    }
    this.getPermissionsForUserByWindow();
  }

  async getPermissionsForUserByWindow() {
    this.userAuthenticated = await firstValueFrom(this.userAuthenticatedService.userObservable.pipe(filter(user => user && user.userId !== 0)));
    this.permissionsUser = await firstValueFrom(this.optionWindowService.getPermissionsForUserByWindow(WindowEnums.InvoicingCutoff));
  }

  public async handleTabChange(tabIndex: number) {
    this.generalHeader.length = this.generalHeader.length === 1 ? 0 : this.generalHeader.length;
    this.detailHeader.length = this.detailHeader.length === 1 ? 0 : this.detailHeader.length;

    if (this.activeTabIndex === tabIndex) return;

    this.page = 1;
    this.limit = 10;
    this.activeTabIndex = tabIndex;

    switch (this.activeTabIndex) {
      case 0:
        this.getIndicatorsForGeneralConsultation();
        await Promise.all([
          this.getColumnsForGeneralConsultation(),
          this.getInvoicingCutoffsWithPagination(),
        ]);
        this.generalData = {
          ...this.generalData,
          currentPage: 1,
          pageSize: 10
        };
        break;
      case 1:
        await Promise.all([
          this.getColumnsForDetailsConsultation(),
          this.getDetailsWithPagination()
        ]);
        this.detailData = {
          ...this.detailData,
          currentPage: 1,
          pageSize: 10
        };
        break;
    }
  }

  onAction(event: { action: string, item?: any, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) { this.router.navigate([`/invoicing-cutoff/register/${event.item.id}`]); }
  }

  async onExport() {
    switch (this.activeTabIndex) {
      case 0:
        await this.exportGeneralConsultation();
        break;
      case 1:
        await this.exportDetailsConsultation();
        break;
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    switch (this.activeTabIndex) {
      case 0:
        await this.getInvoicingCutoffsWithPagination()
        break;
      case 1:
        await this.getDetailsWithPagination()
        break;
    }
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

}
